import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pending, Completed, Inprogress, Cancelled, Line } from "../../icons";
import { fetchStagesNames } from "../../store/slices/stagesSlice";
import "./StagesIndicator.scss";

const StagesIndicator = ({
  currentStageId,
  isTerminal = false,
  previousStageOrder: prevOrderProp,
  stages: stagesProp,
}) => {
  const dispatch = useDispatch();
  const { stagesNamesData, isStagesNamesLoading, stageNameError } = useSelector(
    (state) => state.stages,
  );

  useEffect(() => {
    if (!stagesNamesData && !stagesProp) {
      dispatch(fetchStagesNames());
    }
  }, [dispatch, stagesNamesData, stagesProp]);

  const currentStageOrder = Number(currentStageId);
  const previousStageOrder = Number.isFinite(prevOrderProp)
    ? prevOrderProp
    : null;

  const rawStages = stagesProp || stagesNamesData;

  const stages = Array.isArray(rawStages)
    ? [...rawStages]
        .sort(
          (firstStage, secondStage) =>
            (firstStage.stageOrder ?? firstStage.stageId ?? 0) -
            (secondStage.stageOrder ?? secondStage.stageId ?? 0),
        )
        .map((stage) => ({
          id: stage.stageId,
          label:
            stage.stageOrder != null && !/^request\s+cancelled$/i.test(stage.stageName)
              ? `${stage.stageOrder}. ${stage.stageName}`
              : stage.stageName,
          stageName: stage.stageName,
          stageOrder: stage.stageOrder ?? stage.stageId,
          isTerminal: stage.isTerminal,
        }))
    : [];

  const updatedStages = stages.map((item) => {
    if (!Number.isFinite(currentStageOrder)) {
      return { ...item, status: "pending" };
    }

    const isSkipped =
      previousStageOrder !== null &&
      item.stageOrder > previousStageOrder &&
      item.stageOrder < currentStageOrder;

    if (isSkipped) {
      return { ...item, status: "skipped" };
    }

    if (item.stageOrder < currentStageOrder) {
      return { ...item, status: "completed" };
    }

    if (item.stageOrder === currentStageOrder) {
      if (isTerminal && /cancel/i.test(item.stageName)) {
        return { ...item, status: "cancelled" };
      }
      return { ...item, status: isTerminal ? "completed" : "inprogress" };
    }

    return { ...item, status: "pending" };
  });

  const getStageIcon = (status) => {
    if (status === "cancelled") return <Cancelled />;
    if (status === "completed") return <Completed />;
    if (status === "inprogress") return <Inprogress />;
    return <Pending />; // covers "pending" and "skipped"
  };

  // When the caller passes explicit stages, render them directly and ignore the
  // shared fetch state. Otherwise fall back to the globally fetched stage list.
  const usingFetchedStages = !stagesProp;

  if (usingFetchedStages && isStagesNamesLoading) {
    return <main className="stages-main">Loading stages...</main>;
  }

  if (usingFetchedStages && stageNameError) {
    // stageNameError may be a string or an error object (e.g. the API error
    // body). Never render a raw object as a React child.
    const stageNameErrorMessage =
      typeof stageNameError === "string"
        ? stageNameError
        : stageNameError?.message ||
          stageNameError?.title ||
          "Unable to load stages.";
    return (
      <main className="stages-main">
        Error loading stages: {stageNameErrorMessage}
      </main>
    );
  }

  return (
    <main className="stages-main">
      {updatedStages.length > 0 &&
        updatedStages.map((item, index) => {
          const isLastStage = index === updatedStages.length - 1;
          const nextStage = updatedStages[index + 1];
          const isTerminalStage = (s) =>
            s?.isTerminal ||
            /^request\s+(completed|cancelled)$/i.test(s?.stageName);
          const hideConnector =
            isLastStage ||
            (isTerminalStage(item) && isTerminalStage(nextStage));

          return (
            <section key={item.id} className="stages-section">
              <div className="stages-icon">
                {getStageIcon(item.status)}
                {!hideConnector && (
                  <div
                    className={`stages-line ${
                      item.status === "completed" ? "completed" : ""
                    }`}
                  >
                    <Line />
                  </div>
                )}
              </div>
              <div
                className={`stages-label motif-body2-default-light ${
                  item.status === "completed" ? "" : "stages-label-new"
                }`}
              >
                {item.label}
              </div>
            </section>
          );
        })}
    </main>
  );
};

export default StagesIndicator;
